/**
 * The stateful half of the skill-check composer, shared by DICE.app (`DiceApp.tsx`) and COMMS's
 * dice popover (`CommsDice.tsx`): the three counters, the named source of the current bonus, the
 * `preselect` re-seed, and the live notation derived from them by `checkNotation.ts`'s
 * `composeCheckNotation`. Each surface renders its own layout around it; neither re-derives the
 * notation, so what a preview shows is what ROLL sends.
 */
import { useEffect, useMemo, useState } from "react";
import { composeCheckNotation, labelRoll } from "./checkNotation";

/**
 * A bonus a caller wants pre-chosen when a composer opens: `label` for display (fed into the Bonus
 * stepper's caption), `bonus` the flat integer seeded into the same `bonus` state a row-pick or the
 * stepper itself would set. `bonus: 0` is a real, distinguishable value: callers signal "nothing
 * preselected" by omitting `preselect` (`undefined`) or passing `null`, never by a zero bonus.
 */
export type PreselectBonus = { label: string; bonus: number };

export function useCheckComposer(preselect?: PreselectBonus | null) {
  // Lazy initializers so a composer that mounts ALREADY carrying a preselect paints pre-filled on
  // its first frame, with no 0-then-preselected flash. The effect below covers a new preselect
  // arriving while already mounted.
  const [bonus, setBonusRaw] = useState(() => preselect?.bonus ?? 0);
  const [accuracy, setAccuracy] = useState(0);
  const [difficulty, setDifficulty] = useState(0);
  // The human-readable name of whatever last set `bonus` (a row label, or the preselect's label):
  // display only, never read by the composer. Stepping the Bonus counter by hand clears it, since
  // the shown number no longer matches that named source.
  const [source, setSource] = useState<string | null>(() => preselect?.label ?? null);

  // Re-seed on a NEW preselect while mounted. Checked for presence, not truthiness, so a
  // preselected `bonus: 0` still applies. Callers hand in a fresh object per request
  // (`shell/railDice.ts`), so even re-picking the identical row rebinds this effect.
  useEffect(() => {
    if (preselect != null) {
      setBonusRaw(preselect.bonus);
      setSource(preselect.label);
    }
  }, [preselect]);

  // The live preview IS the send payload: no second formatting path to drift from it.
  const notation = useMemo(() => labelRoll(composeCheckNotation(bonus, accuracy, difficulty), source), [bonus, accuracy, difficulty, source]);

  return {
    bonus,
    accuracy,
    difficulty,
    source,
    notation,
    /** Set the bonus from a named source (a pilot row). */
    pick: (label: string, value: number) => {
      setBonusRaw(value);
      setSource(label);
    },
    /** Set the bonus by hand (the stepper), which drops the named source. */
    setBonus: (value: number) => {
      setBonusRaw(value);
      setSource(null);
    },
    setAccuracy,
    setDifficulty,
    /** Back to the bare `1d20`. */
    reset: () => {
      setBonusRaw(0);
      setSource(null);
      setAccuracy(0);
      setDifficulty(0);
    },
  };
}
